import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buildInventoryLabels, inventoryLabelHtml } from '../utils/inventoryLabels';

export default function InventoryLabelExport({ products, catalogBarcodes, onClose }) {
  const labels = useMemo(() => buildInventoryLabels(products, catalogBarcodes), [products, catalogBarcodes]);
  const [selected, setSelected] = useState(() => new Set(labels.map(label => label.id)));
  const dialog = useRef(null);
  const [error, setError] = useState('');
  useEffect(() => { dialog.current?.showModal?.(); }, []);
  const toggle = ids => setSelected(current => {
    const next = new Set(current);
    const remove = ids.every(id => current.has(id));
    ids.forEach(id => remove ? next.delete(id) : next.add(id));
    return next;
  });
  const quantities = [...new Set(labels.map(label => label.quantity))];
  const chosen = labels.filter(label => selected.has(label.id));
  const exportLabels = () => {
    if (chosen.some(label => !label.sku)) { setError('Some selected labels have no SKU. Uncheck those labels before printing.'); return; }
    const preview = window.open('', '_blank');
    if (!preview) { setError('Allow pop-ups to open the printable labels.'); return; }
    preview.opener = null;
    preview.document.write(inventoryLabelHtml(chosen));
    preview.document.close();
    setError('');
  };
  return <dialog ref={dialog} aria-labelledby="inventory-label-title" onCancel={onClose}
    className="fixed inset-0 m-auto max-h-[90vh] w-[min(960px,95vw)] rounded-xl border bg-white p-5 shadow-xl backdrop:bg-black/40">
    <div className="flex items-center justify-between gap-3">
      <h2 id="inventory-label-title" className="text-lg font-bold">Export Labels</h2>
      <button type="button" autoFocus onClick={onClose} className="rounded border px-3 py-2">Close</button>
    </div>
    <p className="my-3 text-sm text-gray-600">Print on A5 portrait with no page margins: 8 labels per sheet, each 148 mm wide × 1″ high. Rice and pulses use 1 KG, 500 Gms and 250 Gms with the existing stock SKU.</p>
    <fieldset className="rounded border p-3"><legend className="px-1 font-semibold">Quantities</legend>
      <div className="flex flex-wrap gap-4">
        <label><input type="checkbox" checked={labels.length > 0 && chosen.length === labels.length} onChange={() => toggle(labels.map(label => label.id))} /> All quantities</label>
        {quantities.map(quantity => {
          const ids = labels.filter(label => label.quantity === quantity).map(label => label.id);
          return <label key={quantity}><input type="checkbox" checked={ids.every(id => selected.has(id))} onChange={() => toggle(ids)} /> {quantity}</label>;
        })}
      </div>
    </fieldset>
    <div className="my-3 flex items-center justify-between gap-3">
      <p aria-live="polite">{chosen.length} of {labels.length} labels selected</p>
      <button type="button" onClick={exportLabels} disabled={!chosen.length} className="rounded bg-blue-700 px-4 py-2 font-semibold text-white disabled:opacity-50">Open printable labels</button>
    </div>
    {error && <p role="alert" className="mb-3 text-red-700">{error}</p>}
    <div className="grid max-h-[50vh] gap-3 overflow-auto sm:grid-cols-2">
      {labels.map(label => <label key={label.id} className={`flex cursor-pointer items-start gap-2 rounded border p-3 ${selected.has(label.id) ? 'border-blue-400 bg-blue-50' : 'bg-gray-50'}`}>
        <input type="checkbox" aria-label={`${label.name}, ${label.quantity}, ${label.sku || 'missing SKU'}`} checked={selected.has(label.id)} onChange={() => toggle([label.id])} />
        <span className="min-w-0 flex-1"><span lang="te" className="block font-bold">{label.name}</span>
          <span className="my-2 block break-all text-xs">SKU: {label.sku || 'Missing SKU'}</span>
          <span className="block text-right font-bold">{label.quantity}</span></span>
      </label>)}
    </div>
  </dialog>;
}
